import Link from "next/link";
import { useRouter } from "next/router";
import { FC, memo, useCallback, useState } from "react";

import { IndexService, PageStatus, ServiceType } from "@/apis/clients";
import { ServiceStatusBudge } from "@/components/atoms/budge/ServiceStatusBudge";
import { Button } from "@/components/atoms/buttons/Button";
import Dropdown from "@/components/molecules/Dropdown";
import Menu from "@/components/organisms/services/ServiceList/Menu";
import {
  serviceStatusToString,
  serviceTypeToString,
} from "@/libs/enums/enumStrings";
import { NEXT_URL, ServiceDetailTab } from "@/utils/const";
import { format } from "@/utils/date";

import MoreIcon from "/public/icon/more.svg";
import dayjs from "dayjs";

const dateFormat = "yyyy/MM/dd";
import IconPageBlank from "public/icon/page-blank.svg";
import CopyIcon from "public/icon/page-copy.svg";
import { ExternalLink } from "@/components/atoms/links/ExternalLink";
import { ButtonWithIcon } from "@/components/atoms/buttons/ButtonWithIcon";
import { ClipboardCopyButton } from "@/components/molecules/buttons/ClipboardCopyButton";
import IconTopPage from "public/icon/top-page.svg";
import { useUserAccount } from "@/states/auth/selectoers";
import ErrorLimitByPlanModal from "../../modals/ErrorLimitByPlanModal";
import { ServiceTagSettingModal } from "../modals/ServiceTagSettingModal";

interface Props {
  services: IndexService[];
  handlePublishPage?: (serviceId: number, id?: number, bool?: boolean) => void;
  handleDuplicateService: (id: number, status: number) => void;
  handleClickDelete: (service: IndexService) => void | Promise<void>;
}

const ServiceListBase: FC<Props> = ({
  services,
  handlePublishPage,
  handleClickDelete,
  handleDuplicateService,
}) => {
  const router = useRouter();
  const account = useUserAccount();

  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [targetTagSettingServiceId, setTargetTagSettingServiceId] = useState<number | null>(null);

  const getOption = useCallback(
    (service: IndexService) => {
      const detailOption = {
        id: 0,
        child: (
          <div
            className="flex text-xs text-black px-3 py-2 w-full"
            onClick={() =>
              void router.push({
                pathname: NEXT_URL.SERVICES_DETAIL,
                query: { id: service.id, tab: ServiceDetailTab.PAGES },
              })
            }
          >
            詳細
          </div>
        ),
      };

      const duplicateOption = {
        id: 1,
        child: (
          <div
            className="flex text-xs text-black px-3 py-2 w-full"
            onClick={() => void handleDuplicateService(service.id, service.status)}
          >
            複製
          </div>
        ),
      };

      const deleteOption = {
        id: 2,
        child: (
          <div
            className="flex text-xs text-[#F15860] px-3 py-2 w-full"
            onClick={() => void handleClickDelete(service)}
          >
            削除
          </div>
        ),
      };

      const changeToPublicOption = service.type === ServiceType.GENERAL_PURPOSE
        ? {
          id: 3,
          child: (
            <div
              className="flex text-xs text-black px-3 py-2 w-full"
              onClick={() => {
                if (account && Number(account.total_page_free_allowed) >= Number(account?.user_plan_config?.max_page_content_free) && service?.page?.status !== PageStatus.PUBLIC) {
                  setIsPopupVisible(true);
                  return;
                }
                handlePublishPage && void handlePublishPage(service.id, service?.page?.id, service?.page?.status === PageStatus.PUBLIC);
              }}
            >
              {service?.page?.status === PageStatus.PUBLIC ? '非公開に変更' : '公開中に変更'}
            </div>
          ),
        }
        : null;

      const addTagSettingOption = service.type === ServiceType.GENERAL_PURPOSE
        ? {
          id: 4,
          child: (
            <div
              className="flex text-xs text-black px-3 py-2 w-full"
              onClick={() =>
                setTargetTagSettingServiceId(service.id)
              }
            >
              タグ設定
            </div>
          ),
        }
        : null;

      const options = [detailOption];

      if (service.type === ServiceType.GENERAL_PURPOSE) {
        if (changeToPublicOption) {
          options.push(changeToPublicOption);
        }
        if (addTagSettingOption) {
          options.push(addTagSettingOption);
        }
      }

      options.push(
        { ...duplicateOption },
        { ...deleteOption }
      );

      return options;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClickDelete, handleDuplicateService, router, account]
  );

  return (
    <div className="mt-4 space-y-4">
      {services.map((service) => (
        <article
          className="py-6 space-y-4 rounded-lg border border-theme-light md:px-6"
          key={service.id}
        >
          <div className="md:flex justify-between">
            <div className="px-4 pb-4 md:pb-0 md:flex md:px-0">
              {
                service.type === ServiceType.GENERAL_PURPOSE &&
                <div className="mr-2">
                  {service.usage_purpose === 1 ? <IconTopPage /> : <IconPageBlank />}
                </div>
              }
              <Link
                href={{
                  pathname: NEXT_URL.SERVICES_DETAIL,
                  query: {
                    id: service.id,
                    tab: service.type === ServiceType.GENERAL_PURPOSE ? ServiceDetailTab.PAGES : ServiceDetailTab.APPLICANT,
                  },
                }}
              >

                <h3 className="pr-4 text-xl text-primary font-bold break-all">
                  {service.type === ServiceType.GENERAL_PURPOSE && service.usage_purpose === 1 ? 'トップページ' : service.name}
                </h3>

              </Link>
              <ServiceStatusBudge
                text={serviceStatusToString(service.status)}
                statusVal={service.status}
                className="!mt-1"
              />
            </div>
            {
              service.type === ServiceType.GENERAL_PURPOSE ?
                <ul className="px-4 md:px-0 flex md:gap-x-4 gap-x-2 md:ml-8 flex-wrap">
                  <li className="shrink-0 flex items-center text-primary md:w-auto w-full">
                    <ClipboardCopyButton
                      value={service?.page?.url ?? ""}
                      message="コピーしました"
                    >
                      <ButtonWithIcon
                        icon={
                          <CopyIcon className="group-hover:scale-110 transition" />
                        }
                        label="URLコピー"
                        className="group flex-row-reverse bg-inherit !text-primary text-sm font-normal leading-normal md:font-normal hover:underline hover:bg-inherit"
                      />
                    </ClipboardCopyButton>
                  </li>
                  <li className="min-w-[105px]">
                    <Button
                      onClick={() =>
                        void router.push({
                          pathname: NEXT_URL.CONTENT_EDIT_PAGE,
                          query: { id: service?.page?.id },
                        })
                      }
                      label="ページ編集"
                      size="sm-fix"
                    />
                  </li>
                  <li className="min-w-[144px]">
                    <Button
                      label="コンテンツ設定"
                      size="sm-fix"
                      onClick={() =>
                        void router.push({
                          pathname: NEXT_URL.CONTENT_EDIT_SERVICE,
                          query: { id: service.id },
                        })
                      }
                    />
                  </li>
                  <li className="col-span-1">
                    <Dropdown
                      menu={getOption(service)}
                      classNameBox="divide-y-0 px-0 w-[98px] z-10 py-1 "
                    >
                      <span
                        className={`flex items-center justify-center w-6 h-9 cursor-pointer rounded-[4px] hover:bg-[#DFDFE2]`}
                      >
                        <MoreIcon />
                      </span>
                    </Dropdown>
                  </li>
                </ul>
                :
                <ul className="grid grid-cols-10 px-4 md:px-0 md:flex md:gap-x-4 gap-x-2 md:ml-8 ">
                  <li className="min-w-[64px] col-span-3">
                    <Button
                      label="設定"
                      size="sm-fix"
                      onClick={() =>
                        void router.push({
                          pathname: NEXT_URL.CONTENT_EDIT_SERVICE,
                          query: { id: service.id },
                        })
                      }
                    />
                  </li>
                  <li className="min-w-[88px] col-span-3">
                    <Button
                      className="!px-0 "
                      label="申込一覧"
                      size="sm-fix"
                      color="primary-outline"
                      onClick={() =>
                        void router.push({
                          pathname: NEXT_URL.SERVICES_DETAIL,
                          query: {
                            id: service.id,
                            tab: ServiceDetailTab.APPLICANT,
                          },
                        })
                      }
                    />
                  </li>

                  <li className="min-w-[88px] col-span-3">
                    <Button
                      className="!px-0 "
                      label="ページ一覧"
                      size="sm-fix"
                      color="primary-outline"
                      onClick={() =>
                        void router.push({
                          pathname: NEXT_URL.SERVICES_DETAIL,
                          query: {
                            id: service.id,
                            tab: ServiceDetailTab.PAGES,
                          },
                        })
                      }
                    />
                  </li>

                  <li className="col-span-1">
                    <Dropdown
                      menu={getOption(service)}
                      classNameBox="divide-y-0 px-0 w-[98px] z-10 py-1 "
                    >
                      <span
                        className={`flex items-center justify-center w-6 h-9 cursor-pointer rounded-[4px] hover:bg-[#DFDFE2]`}
                      >
                        <MoreIcon />
                      </span>
                    </Dropdown>
                  </li>
                </ul>
            }

          </div>

          <div className=" md:p-0 p-4 md:flex justify-between">
            {service.type === ServiceType.GENERAL_PURPOSE ?
              <ul className="flex flex-col md:flex-row flex-wrap gap-x-4 gap-y-2 mb-2">
                <li className="text-[#1A1A1C] font-bold text-sm flex items-center">
                  <ExternalLink
                    className="block w-full text-sm text-left text-gray-900 font-normal bg-white"
                    href={service?.page?.url || ''}
                    iconClassName="fill-primary md:w-4 md:h-4"
                    isNoUnderline
                  >
                    {service?.page?.name}
                  </ExternalLink>
                </li>
                {
                  service?.page?.secret_flag ? <li className="bg-gray-200 text-gray-500 px-[14px] text-xs font-bold py-1 w-fit rounded-full">
                    <span>アクセス制限あり</span>
                  </li> : null
                }
              </ul>
              :
              <ul className="flex flex-col md:flex-row flex-wrap gap-x-4 gap-y-2 mb-2">
                <li className="text-[#1A1A1C] font-bold text-sm">
                  {serviceTypeToString(service.type)}
                </li>

                {service.type === ServiceType.EVENT_SEMINAR && (
                  <Menu menus={service.details ?? []} />
                )}

                {service.type === ServiceType.CALENDAR_RESERVATION && (
                  <>
                    {(service.reservation_period_from && service.reservation_period_to) ?
                      `${dayjs(service.reservation_period_from).format("YYYY/MM/DD")} - ${dayjs(service.reservation_period_to).format("YYYY/MM/DD")}` : ''}
                  </>
                )}

                {service.type === ServiceType.SUBSCRIPTION_SERVICE ? (
                  <>
                    <li className="text-[#1A1A1C] text-sm font-bold">
                      総申込数
                      <span className="font-normal ml-4">
                        {(service.total_applications ?? 0).toLocaleString()}
                      </span>
                    </li>
                    <li className="text-[#1A1A1C] text-sm font-bold">
                      現契約数
                      <span className="font-normal ml-4">
                        {(service.current_contracts ?? 0).toLocaleString()}
                      </span>
                    </li>
                  </>
                ) : (
                  <li className="text-[#1A1A1C] text-sm font-bold">
                    申込数
                    <span className="font-normal ml-4">
                      {service.applications.toLocaleString()}
                    </span>
                  </li>
                )}

                <li className="text-[#47BDAB] text-sm underline">
                <Link
                  href={{
                    pathname: NEXT_URL.MAILS_CREATE,
                    query: {
                      s: service.id,
                      n: service.name,
                      only_active: true,
                    },
                  }}
                >
                    {service.type === ServiceType.SUBSCRIPTION_SERVICE
                      ? "現契約者へのメール配信"
                      : "メール配信"}
                  </Link>
                </li>
              </ul>
            }

            <p className="text-sm text-[#0E0E0E] whitespace-nowrap md:pl-4 md:text-right">
              <span className="text-[#1A1A1C] font-bold">最終更新日：</span>
              <time>{format(new Date(service.updated_at), dateFormat)}</time>
            </p>
          </div>
        </article>
      ))}
      {isPopupVisible && <ErrorLimitByPlanModal onClose={() => setIsPopupVisible(false)} reason="PAGE_PUBLISH_LIMIT" />}
      {targetTagSettingServiceId && (
        <ServiceTagSettingModal
          serviceId={targetTagSettingServiceId}
          isOpen={true}
          closeHandler={() => setTargetTagSettingServiceId(null)}
          isGeneralPurpose={targetTagSettingServiceId !== null}
        />
      )}
    </div>
  );
};

export const ServiceList = memo(ServiceListBase);
